import { startOfDay, startOfTomorrow } from 'date-fns'
import { useEffect, useState } from 'react'

// Today at 00:00, rolled over when the clock passes midnight.
//
// EXPL: the date pickers take this as `minDate` and are memoized, so it has to keep its identity
// between renders — rebuilding it inline defeats the comparison. But a value frozen at mount leaves
// a composer that sat open overnight offering a day that has meanwhile become the past, so it is
// held in state and moved by a timer. A `Date` with year, month and day set is also what the MUI
// picker wants: `new Date()` as a floor has misbehaved here before.
//
// The timer re-arms itself rather than the effect re-running on the value: a timer that fires early
// (clock stepped back by NTP/DST, or a UA firing marginally early) then leaves the day unchanged, so
// keeping the previous Date both preserves its identity and avoids an effect that would re-arm
// itself with a ~0ms delay and spin until midnight.
export function useTodayStart(): Date {
  const [today, setToday] = useState(() => startOfDay(new Date()))

  useEffect(() => {
    let timer = 0

    const scheduleRollover = () => {
      timer = window.setTimeout(() => {
        const next = startOfDay(new Date())
        setToday(prev => (prev.getTime() === next.getTime() ? prev : next))
        scheduleRollover()
      }, Math.max(startOfTomorrow().getTime() - Date.now(), 0))
    }

    scheduleRollover()
    return () => window.clearTimeout(timer)
  }, [])

  return today
}

export default useTodayStart
